import { computed, reactive, ref } from 'vue';
import { createConsumer } from '@rails/actioncable';

const MESSAGE_TYPE = { INCOMING: 0, OUTGOING: 1, ACTIVITY: 2, TEMPLATE: 3 };
const KIND_BY_MESSAGE_TYPE = {
  [MESSAGE_TYPE.INCOMING]: 'visitor',
  [MESSAGE_TYPE.ACTIVITY]: 'activity',
};
const REPLY_WAIT_MS = 15000;
// Chatwoot's own email-capture and CSAT forms need UI this embed does not have yet; showing their bare
// prompt text ("Get notified by email") without the form would be a dead end.
const UNSUPPORTED_CONTENT_TYPES = ['input_email', 'input_csat'];
const HIDDEN_LIMIT = 20;

const randomId = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

const normalize = raw => ({
  id: raw.id,
  echoId: raw.echo_id || null,
  kind: KIND_BY_MESSAGE_TYPE[raw.message_type] || 'agent',
  content: raw.content || '',
  contentType: raw.content_type || 'text',
  items: raw.content_attributes?.items || [],
  submitted: raw.content_attributes?.submitted_values?.[0] || null,
  attachments: raw.attachments || [],
  sender: raw.sender
    ? {
        name: raw.sender.name || raw.sender.available_name,
        avatar: raw.sender.avatar_url || raw.sender.thumbnail,
      }
    : null,
  createdAt: raw.created_at,
  pending: false,
  failed: false,
});

// One visitor conversation over the widget API + the contact's ActionCable stream.
export const useConversation = ({ api, storage, wsUrl, isOpen }) => {
  const status = ref('idle');
  const messages = ref([]);
  const awaitingReply = ref(false);
  const agentTyping = ref(false);
  const unread = ref(0);
  const hiddenEchoIds = new Set();
  let hiddenIds = new Set(storage.get().hidden || []);
  let replyTimer = null;
  let connecting = null;
  let consumer = null;

  const expectReply = () => {
    awaitingReply.value = true;
    clearTimeout(replyTimer);
    replyTimer = setTimeout(() => {
      awaitingReply.value = false;
    }, REPLY_WAIT_MS);
  };

  const replyArrived = () => {
    awaitingReply.value = false;
    clearTimeout(replyTimer);
  };

  const rememberHidden = id => {
    hiddenIds = new Set([...hiddenIds, id].slice(-HIDDEN_LIMIT));
    storage.set({ hidden: [...hiddenIds] });
  };

  const upsert = raw => {
    const incoming = normalize(raw);
    const index = messages.value.findIndex(
      message =>
        message.id === incoming.id ||
        (incoming.echoId && message.echoId === incoming.echoId)
    );
    if (index === -1) {
      messages.value = [...messages.value, incoming].sort(
        (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
      );
      return true;
    }
    const next = [...messages.value];
    next[index] = { ...next[index], ...incoming };
    messages.value = next;
    return false;
  };

  const markSeen = () => {
    if (isOpen.value) api.markSeen().catch(() => {});
  };

  const onCableEvent = ({ event, data }) => {
    if (event === 'message.created') {
      const isNew = upsert(data);
      if (data.message_type !== MESSAGE_TYPE.INCOMING && !data.private) {
        replyArrived();
        agentTyping.value = false;
        if (isNew && !isOpen.value) unread.value += 1;
        markSeen();
      }
    } else if (event === 'message.updated') {
      upsert(data);
    } else if (event === 'conversation.typing_on' && !data.is_private) {
      agentTyping.value = true;
    } else if (event === 'conversation.typing_off') {
      agentTyping.value = false;
    }
  };

  const loadMessages = async () => {
    const { payload } = await api.messages();
    messages.value = payload.map(normalize);
  };

  const subscribe = pubsubToken => {
    consumer = createConsumer(wsUrl);
    let connectedOnce = false;
    consumer.subscriptions.create(
      { channel: 'RoomChannel', pubsub_token: pubsubToken },
      {
        received: onCableEvent,
        // A reconnect may have missed messages; the history endpoint is the source of truth.
        connected: () => {
          if (connectedOnce) loadMessages().catch(() => {});
          connectedOnce = true;
        },
      }
    );
  };

  const connect = () => {
    if (connecting) return connecting;
    status.value = 'connecting';
    connecting = (async () => {
      // A returning visitor skips config: it is rate limited per inbox (config/initializers/rack_attack.rb),
      // so only new visitors should count against it. A 404 means the stored token expired (180 days).
      const stored = storage.get();
      if (stored.authToken && stored.pubsubToken) {
        api.setAuthToken(stored.authToken);
        try {
          await loadMessages();
          subscribe(stored.pubsubToken);
          status.value = 'ready';
          return;
        } catch (error) {
          if (error.status !== 404) throw error;
          api.setAuthToken(null);
        }
      }
      const config = await api.config();
      api.setAuthToken(config.website_channel_config.auth_token);
      storage.set({
        authToken: config.website_channel_config.auth_token,
        pubsubToken: config.contact.pubsub_token,
      });
      await loadMessages();
      subscribe(config.contact.pubsub_token);
      status.value = 'ready';
    })().catch(error => {
      connecting = null;
      status.value = 'error';
      throw error;
    });
    return connecting;
  };

  const send = async (content, { hidden = false } = {}) => {
    const echoId = randomId();
    if (hidden) hiddenEchoIds.add(echoId);
    messages.value = [
      ...messages.value,
      {
        ...normalize({
          content,
          message_type: MESSAGE_TYPE.INCOMING,
          created_at: Date.now() / 1000,
        }),
        id: null,
        echoId,
        pending: true,
      },
    ];
    expectReply();
    try {
      const saved = await api.sendMessage({ content, echoId });
      if (hidden) rememberHidden(saved.id);
      upsert({ ...saved, echo_id: echoId });
    } catch {
      replyArrived();
      messages.value = messages.value.map(message =>
        message.echoId === echoId
          ? { ...message, pending: false, failed: true }
          : message
      );
    }
  };

  const retry = message => {
    messages.value = messages.value.filter(
      item => item.echoId !== message.echoId
    );
    return send(message.content);
  };

  const choose = async (message, option) => {
    messages.value = messages.value.map(item =>
      item.id === message.id
        ? { ...item, submitted: { title: option.title, value: option.value } }
        : item
    );
    expectReply();
    try {
      await api.submitChoice(message.id, option);
    } catch {
      replyArrived();
      messages.value = messages.value.map(item =>
        item.id === message.id ? { ...item, submitted: null } : item
      );
    }
  };

  const startIfEmpty = async startMessage => {
    if (startMessage && messages.value.length === 0)
      await send(startMessage, { hidden: true });
  };

  const visibleMessages = computed(() =>
    messages.value.filter(
      message =>
        !hiddenIds.has(message.id) &&
        !hiddenEchoIds.has(message.echoId) &&
        !UNSUPPORTED_CONTENT_TYPES.includes(message.contentType)
    )
  );

  // The tray only offers the menu the bot is waiting on: the newest message, unanswered.
  const activeChoices = computed(() => {
    const last = visibleMessages.value[visibleMessages.value.length - 1];
    if (
      !last ||
      last.kind !== 'agent' ||
      last.contentType !== 'input_select' ||
      last.submitted
    )
      return null;
    return last.items.length ? last : null;
  });

  const disconnect = () => consumer?.disconnect();

  return reactive({
    status,
    visibleMessages,
    activeChoices,
    awaitingReply,
    agentTyping,
    unread,
    connect,
    send,
    retry,
    choose,
    startIfEmpty,
    markSeen,
    disconnect,
  });
};
